// Atribución de marketing que tiene que sobrevivir el salto nexvia.io → app.nexvia.io.
//
// GEMELO DE nexvia-landing/src/utils/attribution.ts. Los dos repos no comparten paquete de
// utilidades, así que el módulo está duplicado a propósito. Si cambia el formato de la
// cookie hay que tocar los dos: la landing la escribe y esta copia la lee.
//
// Va en cookie y no en localStorage porque localStorage es por origen: lo que escribe
// nexvia.io es invisible para app.nexvia.io. Una cookie en el dominio registrable
// (.nexvia.io) la ven los dos. Google ya hace exactamente esto con _gcl_aw — verificado el
// 2026-09-16: se aterrizó en nexvia.io/?gclid=X y esa misma cookie, con el mismo valor, se
// leyó después desde app.nexvia.io.
//
// ── Dos fotos, no dos políticas ──
//
// Se guardan `firstTouch` y `lastTouch`, cada uno con el esquema COMPLETO. Cada foto sale de
// un único aterrizaje, así que es internamente coherente. La versión anterior mezclaba
// políticas entre familias —UTMs first-touch, identificadores de clic last-touch— y eso
// produce filas que describen un recorrido que nunca existió: el auto-tagging de Google Ads
// NO agrega utm_*, así que un gclid de Ads terminaba conviviendo con los UTMs de una visita
// orgánica posterior. Es el caso común, no el raro.
//
// Con las dos fotos guardadas se puede calcular cualquier modelo de atribución después; con
// una sola política la información ya se perdió al escribir. Es lo que hacen GA4 (dimensiones
// user-scoped vs session-scoped) y HubSpot (Original source vs Latest source).
//
// Y no, la política NO se elige "porque Ads atribuye por last-click": desde 2025 el modelo
// por defecto de Ads es data-driven y los de reglas están deprecados. Además Ads nunca lee
// esta cookie — usa su propio gclid, el de _gcl_aw. Esto es para NUESTROS registros.
//
// ── Límite conocido, escrito a propósito ──
//
// Safari: ITP topa en 7 días toda cookie escrita desde JavaScript, y en 24 horas cuando la
// navegación viene de un dominio clasificado como rastreador y la URL trae query string —
// o sea, exactamente un clic de Google Ads. El max-age de abajo NO se cumple ahí. Decisión
// tomada: se documenta, y el arreglo real es persistir la atribución en el backend al crear
// la solicitud, que es lo único que sobrevive al navegador.

const COOKIE_NAME = 'nx_attr';
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60; // 90 días donde el navegador lo respete
const MAX_VALUE_LENGTH = 120;
// Los navegadores descartan la cookie ENTERA, en silencio, cuando pasa de ~4096 bytes. Se
// deja margen y se prefiere perder lastTouch antes que perderlo todo.
const MAX_COOKIE_BYTES = 3500;

/** Vienen así en la URL — convención de Google, no nuestra. Se traducen al leerlos. */
const CLICK_ID_PARAMS = { gclid: 'gclid', gbraid: 'gbraid', wbraid: 'wbraid' } as const;
const UTM_PARAMS = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent'
} as const;

export interface Touch {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /** ISO. Cuándo se vio este aterrizaje. */
  capturedAt?: string;
}

export interface Attribution {
  firstTouch?: Touch;
  lastTouch?: Touch;
}

/** Las únicas claves que pueden salir de la cookie. Todo lo demás se descarta al leer. */
const TOUCH_KEYS: (keyof Touch)[] = [
  'gclid',
  'gbraid',
  'wbraid',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'capturedAt'
];

function sanitize(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  // Los valores salen de la query string o de la cookie, o sea del visitante: cualquiera
  // puede llegar con ?utm_source=<script>. Los escapes van escritos como escapes y no como
  // los bytes crudos: un literal con los caracteres de control adentro vuelve binarios el
  // fuente y el bundle, y git deja de poder diffearlos.
  // eslint-disable-next-line no-control-regex
  const clean = value.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return clean ? clean.slice(0, MAX_VALUE_LENGTH) : undefined;
}

/** Filtra un objeto cualquiera a la forma Touch. Devuelve undefined si no queda nada. */
function toTouch(raw: unknown): Touch | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const touch: Touch = {};
  for (const key of TOUCH_KEYS) {
    const value = sanitize(typeof source[key] === 'string' ? (source[key] as string) : null);
    if (value) touch[key] = value;
  }
  return Object.keys(touch).length > 0 ? touch : undefined;
}

/**
 * `.nexvia.io` en producción y en cualquier subdominio; sin atributo `domain` fuera de ahí,
 * para que la cookie siga funcionando en localhost y en los previews de CI.
 */
function cookieDomain(hostname: string): string | undefined {
  return hostname === 'nexvia.io' || hostname.endsWith('.nexvia.io') ? '.nexvia.io' : undefined;
}

function serialize(value: Attribution): string {
  return `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}`;
}

function readCookie(): Attribution | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw.slice(COOKIE_NAME.length + 1)));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Attribution;
  } catch {
    // Cookie corrupta o de un formato viejo: se la trata como ausente y la próxima captura
    // la reescribe. Nunca hacemos fallar la app por esto.
    return null;
  }
}

function writeCookie(value: Attribution): void {
  if (typeof document === 'undefined') return;

  // Si no entra, se suelta lastTouch antes que perder la cookie entera: firstTouch es el
  // dato que no se puede volver a obtener, lastTouch se reescribe en la visita siguiente.
  let payload = value;
  if (serialize(payload).length > MAX_COOKIE_BYTES && payload.firstTouch) {
    payload = { firstTouch: payload.firstTouch };
  }
  if (serialize(payload).length > MAX_COOKIE_BYTES) {
    if (process.env.NODE_ENV !== 'production') console.warn('[attribution] atribución demasiado grande, no se guarda');
    return;
  }

  const domain = cookieDomain(window.location.hostname);
  const parts = [serialize(payload), 'path=/', `max-age=${MAX_AGE_SECONDS}`, 'SameSite=Lax'];
  if (domain) parts.push(`domain=${domain}`);
  // Secure rompería la cookie en el dev server, que corre sobre http.
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
}

/**
 * Lee la atribución guardada, filtrada por lista blanca. Devuelve `{}` si no hay nada —
 * nunca null, para que quien la consuma pueda desestructurarla sin guardas.
 *
 * El filtro no es cosmético: la cookie vive en .nexvia.io y la puede escribir cualquier
 * script de cualquier subdominio. Sin lista blanca, una cookie con claves arbitrarias las
 * inyecta en el dataLayer, que es de donde el contenedor lee sus variables.
 */
export function readAttribution(): Attribution {
  const stored = readCookie();
  if (!stored) return {};
  const clean: Attribution = {};
  const first = toTouch(stored.firstTouch);
  const last = toTouch(stored.lastTouch);
  if (first) clean.firstTouch = first;
  if (last) clean.lastTouch = last;
  return clean;
}

/**
 * Captura los parámetros de campaña de la URL actual y actualiza la cookie.
 * Idempotente: llamarla en cada carga es lo esperado.
 *
 * Un aterrizaje cuenta como *toque* solo si trae un identificador de clic o `utm_source`.
 * Sin esa condición, un `?utm_content=firma-mail` de un enlace interno o de un QR se quedaba
 * con el first-touch y bloqueaba la campaña real durante 90 días.
 */
export function captureAttribution(search?: string): Attribution {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(search ?? window.location.search);
  const stored = readAttribution();

  const touch: Touch = {};
  for (const [param, key] of Object.entries(CLICK_ID_PARAMS) as [string, keyof Touch][]) {
    const value = sanitize(params.get(param));
    if (value) touch[key] = value;
  }
  for (const [param, key] of Object.entries(UTM_PARAMS) as [string, keyof Touch][]) {
    const value = sanitize(params.get(param));
    if (value) touch[key] = value;
  }

  const esToque = Boolean(touch.gclid || touch.gbraid || touch.wbraid || touch.utmSource);
  if (!esToque) return stored;

  touch.capturedAt = new Date().toISOString();
  const next: Attribution = { firstTouch: stored.firstTouch ?? touch, lastTouch: touch };
  writeCookie(next);
  return next;
}
